import { useMemo, useState, KeyboardEvent } from "react";
import { useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { CustomTypography } from "../../../components/CustomTypography/CustomTypography";
import { Icon } from "../../../components/Icon/Icon";
import { colors } from "../../../styles/colors";
import { CustomTextInput } from "../../../components/CustomTextInput/CustomTextInput";
import { CustomButton } from "../../../components/CustomButton/CustomButton";
import {
  useCreateDepartment,
  useGetAllDepartments,
} from "../../../api/departments";
import { getCurrentFleetId, isFleetAdmin } from "../../../utils/getCurrentFleet";
import { ModalDeleteService } from "./ModalDeleteService/ModalDeleteService";
import useStyles from "./styles";

export const ParametreService = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const companyId = getCurrentFleetId();
  const canEdit = isFleetAdmin();
  const isMobile = useMediaQuery("(max-width:599.95px)");

  const { departments, isLoading } = useGetAllDepartments(companyId);
  const { mutate: createDepartment, isPending } = useCreateDepartment(
    companyId,
    () => setName("")
  );

  const [name, setName] = useState("");
  const [serviceToDelete, setServiceToDelete] = useState<TDepartment | null>(
    null
  );

  const existingNames = useMemo(
    () => new Set(departments.map((d) => d.name.trim().toLowerCase())),
    [departments]
  );

  const onAdd = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error(t("setting.serviceNameRequired", "Please enter a service name"));
      return;
    }
    if (existingNames.has(trimmed.toLowerCase())) {
      toast.error(t("setting.serviceExists", "This service already exists"));
      return;
    }
    createDepartment({ name: trimmed });
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onAdd();
    }
  };

  return (
    <div className={classes.divDepartement}>
      <ModalDeleteService
        open={!!serviceToDelete}
        service={serviceToDelete}
        onClose={() => setServiceToDelete(null)}
        onCancel={() => setServiceToDelete(null)}
      />

      {/* title */}
      <CustomTypography
        text={t("setting.departement", "Departement")}
        variant={"h3"}
        isUppercase
      />

      {/* add service */}
      {canEdit && (
        <div className={classes.divButtonTVA} onKeyDown={onKeyDown}>
          <CustomTextInput
            label={t("setting.service", "Service")}
            placeholder={t("setting.placeholderService", "YOUR_SERVICE")}
            value={name}
            name="service"
            onChange={(e) => setName(e.target.value)}
          />
          <CustomButton
            label={t("common.add", "Add")}
            variant={name.trim() && !isPending ? "primary" : "disabled"}
            disabled={!name.trim() || isPending}
            loading={isPending}
            width={isMobile ? "100%" : "fit-content"}
            height={"40px"}
            onClick={onAdd}
          />
        </div>
      )}

      {/* liste des services */}
      <div className={classes.divListService}>
        <div className={classes.serviceList}>
          <CustomTypography
            text={t("setting.list", "List of your services")}
            variant={"default"}
            fontWeight="bold"
          />
          {!isLoading && (
            <CustomTypography
              text={String(departments.length)}
              variant={"link"}
              color={colors.mutedText}
            />
          )}
        </div>

        {isLoading ? (
          <div className={classes.divServiceList}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className={classes.skeletonChip} />
            ))}
          </div>
        ) : departments.length === 0 ? (
          <div className={classes.emptyState}>
            <CustomTypography
              text={t(
                "setting.noService",
                "No service yet. Add your first one above."
              )}
              variant={"default"}
            />
          </div>
        ) : (
          <div className={classes.divServiceList}>
            {departments.map((service) => (
              <div className={classes.divService} key={service.id ?? service.name}>
                <CustomTypography text={service.name} variant={"link"} />
                {canEdit && (
                  <button
                    type="button"
                    className={classes.chipDeleteBtn}
                    aria-label={
                      t("common.delete", "Delete") + " " + service.name
                    }
                    onClick={() => setServiceToDelete(service)}
                  >
                    <Icon name={"close"} width={"15px"} height={"15px"} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
